/**
 * LOS CULTIVOS COMO LOS ESCRIBE EL CUADERNO (CU2, 27-09-2026).
 *
 * El 2.1 y el 3.1 del modelo de la Junta piden la ESPECIE en castellano. En Pest
 * el cultivo se escribe a mano en el ciclo o en el sector, en el idioma de quien
 * lo da de alta: el cuaderno de la demo salía con «Pomodoro», «Lattuga»,
 * «tomate» y «Brocoli». Esta tabla pura pasa cada especie a su nombre oficial en
 * castellano, con sus sinónimos en italiano, inglés, francés y portugués, sin
 * mirar tildes ni mayúsculas; y con los nombres de los cultivos por defecto de la
 * app en los nueve idiomas (chino, árabe, bambara y mandinga incluidos).
 *
 * Es la lista de los cultivos por defecto de la app (`cropCycles.defaultCrops`
 * en los idiomas de i18n: tomato, cucumber, pepper, watermelon, melon, eggplant,
 * zucchini, bean, lettuce; el test comprueba que TODAS sus traducciones caen
 * aquí) más los hortícolas habituales del invernadero almeriense.
 *
 * Lo que no se reconoce NO se inventa: sale tal cual, con la mayúscula inicial,
 * y el motor lo marca como hueco suave («sin confirmar») en la hoja de revisión.
 *
 * PURO: sin red; su único import es la lista curada (también pura).
 */
import { CULTIVOS_CURADOS, cultivoCuradoPorCodigo } from "../crops/curados";

/* La TABLA sale de la lista curada única (src/lib/crops/curados.ts, 01-10-2026,
   plan cultivos SIEX): ya no se escribe aquí. Son SOLO estas 27 especies y en
   este orden: el orden de `CULTIVOS_OFICIALES` y el «primero gana» del índice
   no cambian, y los cultivos curados nuevos (acelga, haba, guisante, fresón)
   no entran — el cuaderno sale idéntico byte a byte. Ampliarla es una decisión
   del cuaderno, no un efecto lateral de curar un cultivo. Lo vigila
   src/__tests__/crops/cuadernoTabla.identica.test.ts. */
const ESPECIES_CUADERNO = [
  "Tomate", "Pimiento", "Pepino", "Berenjena", "Calabacín", "Melón", "Sandía", "Judía", "Lechuga",
  "Brócoli", "Coliflor", "Col", "Calabaza", "Fresa", "Espárrago", "Espinaca", "Cebolla", "Ajo", "Patata",
  "Zanahoria", "Apio", "Alcachofa", "Arándano", "Frambuesa", "Aguacate", "Mango", "Níspero",
] as const;

/** Nombre oficial → sinónimos (sin tildes, en minúsculas; el propio nombre ya cuenta). Acepta los tres cajones: nombres, erratas y tipos. */
const TABLA: Record<string, string[]> = Object.fromEntries(
  ESPECIES_CUADERNO.map((visible) => {
    const c = CULTIVOS_CURADOS.find((x) => x.visible === visible);
    if (!c) throw new Error(`cuaderno: falta «${visible}» en CULTIVOS_CURADOS`);
    return [visible, [...c.nombres, ...(c.erratas ?? []), ...(c.tipos ?? [])]];
  }),
);

/** Minúsculas, sin tildes, sin signos y con un solo espacio: la clave de búsqueda. */
export function claveCultivo(s: string | null | undefined): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N} -]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const INDICE: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const [oficial, sinonimos] of Object.entries(TABLA)) {
    for (const s of [oficial, ...sinonimos]) {
      const k = claveCultivo(s);
      if (k && !m.has(k)) m.set(k, oficial);
    }
  }
  return m;
})();

/** Los nombres oficiales que conoce la tabla (para los tests y la ficha). */
export const CULTIVOS_OFICIALES: readonly string[] = Object.keys(TABLA);

export interface EspecieOficial {
  /** Lo que se imprime: el nombre oficial, o lo escrito con mayúscula inicial. null = no hay cultivo. */
  nombre: string | null;
  /** false = no está en la tabla: sale tal cual y se marca como hueco suave. */
  reconocida: boolean;
}

/**
 * «pomodoro» → Tomate; «Brocoli» → Brócoli; «Tomate Cherry» → Tomate; «Kiwano» → Kiwano (no reconocida).
 * Con `codigo` (el código SIEX del ciclo, plan cultivos SIEX): si es de un
 * cultivo curado manda el código y sale su nombre visible (166 → «Acelga»),
 * escriba lo que escriba el texto; un código desconocido no cambia nada.
 */
export function especieOficial(raw: string | null | undefined, codigo?: number | null): EspecieOficial {
  const curado = cultivoCuradoPorCodigo(codigo);
  if (curado) return { nombre: curado.visible, reconocida: true };
  const limpio = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!limpio) return { nombre: null, reconocida: false };
  const oficial = INDICE.get(claveCultivo(limpio));
  if (oficial) return { nombre: oficial, reconocida: true };
  return { nombre: limpio.charAt(0).toLocaleUpperCase("es") + limpio.slice(1), reconocida: false };
}
